import CHOICES from "CHOICES";
import { mutate } from "utils/relay";
import {
  AUTH_TOKEN,
  DEFAULT_IATA_ID,
  DEFAULT_IATA_NUMBER,
  ENV_MAPPER,
  IATA_LIST,
  REFRESH_TOKEN,
  TOKEN_TTL,
  DEFAULT_IATA_CURRENCY,
  DEFAULT_IATA_REFERENCE,
} from "utils/constants";
import {
  emptyProxyObject,
  getEnv,
  getSessionStorage,
  ProxyObjectType,
} from "utils/utils";
import { Action, Thunk, Computed, thunk, action, computed } from "easy-peasy";
import { Cookies } from "react-cookie";
import graphql from "babel-plugin-relay/macro";
import moment from "moment";

const mutation = graphql`
  mutation authRefreshMutation($input: RefreshInput!) {
    refreshToken(input: $input) {
      token
      payload
      refreshToken
    }
  }
`;

const cookie = new Cookies();

const env = getEnv();

const cookieDomain = (env: string) =>
  env === ENV_MAPPER.production
    ? ".v3.nuflights.com"
    : env === ENV_MAPPER.development
    ? `.${env}.nuflights.com`
    : `.${env}.llc.nuflights.com`;

const UK = CHOICES.UserKind;

export interface CurrentUserModel {
  username: string;
  email: string;
}

export interface AuthModel {
  currentUser: CurrentUserModel | ProxyObjectType;
  authToken: string | null;
  publicKey: string | null;
  iatas: string | null;
  validUserIata: boolean;
  defaultIata: string | null;
  defaultIataID: string | null;
  isLoggedIn: Computed<AuthModel, boolean>;
  isAdmin: Computed<AuthModel, boolean>;
  setAuthToken: Action<AuthModel, string | null>;
  setValidUserIata: Action<AuthModel, string | boolean>;
  setIataNumber: Action<AuthModel, string | null>;
  setDefaultIataNumber: Action<AuthModel, string | null>;
  setDefaultIataID: Action<AuthModel, string | null>;
  setCurrentUser: Action<AuthModel, CurrentUserModel | ProxyObjectType>;
  updateAuthToken: Thunk<AuthModel, any>;
  setDefaultIataNumbers: Thunk<AuthModel, string>;
  clearAuthToken: Thunk<AuthModel>;
  fetchAuthToken: Thunk<AuthModel, any>;
  initRefreshToken: Thunk<AuthModel, boolean>;
  refreshIntervalId: NodeJS.Timeout | null;
  setRefreshIntervalId: Action<AuthModel, NodeJS.Timeout | null>;
  permissions: any;
  setPermissions: Action<AuthModel, any>;
  updatePermissions: Thunk<AuthModel, any>;
  refreshToken: string | null;
  setRefreshToken: Action<AuthModel, string | null>;
  updateRefreshToken: Thunk<AuthModel, any>;
  clearRefreshToken: Thunk<AuthModel>;
  clearPublicKey: Thunk<any>;
  defaultIataCurrency: string | null;
  setDefaultIataCurrency: Action<AuthModel, string | null>;
  setIATAREFERENCE: Action<AuthModel, string | null>;
  clearIatas: Action<AuthModel>;
}

const AuthStore: AuthModel = {
  validUserIata: false,
  permissions: {},
  currentUser: emptyProxyObject,
  authToken: cookie.get(AUTH_TOKEN),
  publicKey: cookie.get("publicKey"),
  iatas: getSessionStorage(IATA_LIST),
  defaultIata: window.sessionStorage.getItem(DEFAULT_IATA_NUMBER),
  defaultIataID: window.sessionStorage.getItem(DEFAULT_IATA_ID),
  defaultIataCurrency: window.sessionStorage.getItem(DEFAULT_IATA_CURRENCY),
  refreshToken: cookie.get(REFRESH_TOKEN),
  refreshIntervalId: null,

  isLoggedIn: computed((state: any) => state.currentUser !== emptyProxyObject),
  isAdmin: computed((state: any) => state.currentUser.kind === UK.ADMIN),

  setAuthToken: action((state: any, authToken) => {
    state.authToken = authToken;
  }),

  setRefreshToken: action((state: any, refreshToken) => {
    state.refreshToken = refreshToken;
  }),

  setValidUserIata: action((state: any, currentState: boolean) => {
    state.validUserIata = Boolean(currentState);
  }),

  setPermissions: action((state: any, permissions) => {
    state.permissions = permissions;
  }),

  setIataNumber: action((state: any, iatas: string) => {
    const localIatas = JSON.parse(iatas) || [];
    const iataNumber =
      localIatas?.find((edge: any) => edge.node.isDefault)?.node?.number ||
      localIatas[0]?.node?.number ||
      null;
    const iataID = localIatas.find(
      (iata: any) => iata?.node?.number === iataNumber
    )?.node?.id;
    window.sessionStorage.setItem(DEFAULT_IATA_NUMBER, iataNumber);
    // window.sessionStorage.setItem(DEFAULT_IATA_ID, iataID);
    window.sessionStorage.setItem(IATA_LIST, String(iatas));
    state.iatas = JSON.parse(iatas);
    state.defaultIata = iataNumber;
    state.defaultIataID = iataID;
  }),

  setCurrentUser: action((state: any, currentUser) => {
    state.currentUser = currentUser;
  }),

  setRefreshIntervalId: action((state: any, intervalId) => {
    state.refreshIntervalId = intervalId;
  }),

  updatePermissions: thunk((actions, permissions) => {
    let permissionsObj: any = {};
    permissions &&
      permissions
        ?.split(" ")
        ?.map((permission: string) => (permissionsObj[permission] = true));
    actions.setPermissions(permissionsObj);
  }),

  updateAuthToken: thunk((actions, authToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(AUTH_TOKEN, authToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          maxAge: Math.floor(authToken?.expiry / 1000),
          // maxAge: authToken?.expiry,
        })
      : cookie.set(AUTH_TOKEN, authToken.self, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
    actions.setAuthToken(authToken.self);
  }),

  updateRefreshToken: thunk((actions, refreshToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(REFRESH_TOKEN, refreshToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          maxAge: Math.floor(refreshToken?.expiry / 1000),
          // maxAge: refreshToken?.expiry,
        })
      : cookie.set(REFRESH_TOKEN, refreshToken.self, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
    actions.setRefreshToken(refreshToken.self);
  }),

  setDefaultIataNumber: action((state: any, iataNumber: string) => {
    state.defaultIata = iataNumber;
    window.sessionStorage.setItem(DEFAULT_IATA_NUMBER, iataNumber);
  }),

  setDefaultIataID: action((state: any, iataID: string) => {
    state.defaultIataID = iataID;
    window.sessionStorage.setItem(DEFAULT_IATA_ID, iataID);
  }),

  setDefaultIataNumbers: thunk((actions, iatas) => {
    actions.setIataNumber(iatas);
  }),

  setDefaultIataCurrency: action((state: any, iataCurrency: string) => {
    state.defaultIataCurrency = iataCurrency;
    window.sessionStorage.setItem(DEFAULT_IATA_CURRENCY, iataCurrency);
  }),
  setIATAREFERENCE: action((state: any, iataReference: string) => {
    state.defaultIataReference = iataReference;
    window.sessionStorage.setItem(DEFAULT_IATA_REFERENCE, iataReference);
  }),
  clearAuthToken: thunk((actions, payload, { getState }) => {
    // Clear any existing refresh interval
    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    window.localStorage.clear();
    window.sessionStorage.clear();
    env !== ENV_MAPPER.local
      ? cookie.remove(AUTH_TOKEN, {
          domain: cookieDomain(env),
          path: "/",
        })
      : cookie.remove(AUTH_TOKEN, { path: "/" });
    actions.setAuthToken(null);
    actions.clearPublicKey();
    actions.setCurrentUser(emptyProxyObject);
  }),

  clearRefreshToken: thunk((actions, payload, { getState }) => {
    // Clear any existing refresh interval
    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    window.localStorage.clear();
    getEnv() !== ENV_MAPPER.local
      ? cookie.remove(REFRESH_TOKEN, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.remove(REFRESH_TOKEN, { path: "/" });
    actions.setRefreshToken(null);
    actions.setCurrentUser(emptyProxyObject);
  }),

  clearPublicKey: thunk(() => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.remove("publicKey", {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : cookie.remove("publicKey", { path: "/" });
  }),

  fetchAuthToken: thunk(
    (actions, { mutation, input, onSuccess, onFailure }) => {
      mutate({
        mutation,
        input: input,
        onSuccess: function (data: any) {
          let iatas;
          let token: any = {
            self: "",
            expiry: new Date(),
          };
          let refreshToken: any = {
            self: "",
            expiry: new Date(),
          };
          let unixTime = Number(moment.unix(data.login.refreshExpiresIn || 0));
          let date = new Date(unixTime);
          if (data.login) {
            token.self = data.login.token;
            token.expiry = date;
            refreshToken.self = data.login.refreshToken;
            refreshToken.expiry = date;
            iatas = JSON.stringify(data.login.user.iatas.edges);
          } else if (data.socialAuth) {
            token = data.socialAuth.token;
          }
          actions.updateAuthToken(token);
          actions.updateRefreshToken(refreshToken);
          actions.setDefaultIataNumbers(String(iatas));
          onSuccess(data);
        },
        onFailure: function (messages: []) {
          console.log("Failed in fetching tokens wrk-bnch");
          actions.clearAuthToken();
          actions.clearRefreshToken();
          onFailure(messages);
        },
      });
    }
  ),

  initRefreshToken: thunk((actions, initialFetch = true, { getState }) => {
    // Clear any existing interval to prevent multiple intervals
    const state = getState();
    if (state.refreshIntervalId) {
      console.log("🔄 Clearing existing refresh interval before creating new one");
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }
  
    // Helper to convert JWT exp -> JS Date correctly
    const getExpiryDate = (exp: number) => {
      const expiryMs = (exp || 0) * 1000; // FIX: convert seconds → milliseconds
      return new Date(expiryMs);
    };
  
    // First refresh on load
    if (initialFetch && cookie.get(REFRESH_TOKEN)) {
      mutate({
        mutation,
        input: { refreshToken: cookie.get(REFRESH_TOKEN) },
        onSuccess: function (data: any) {
          const exp = data.refreshToken.payload.exp;
          const expiryDate = getExpiryDate(exp);
          actions.updateAuthToken({
            self: data.refreshToken.token,
            expiry: expiryDate,
          });
  
          actions.updateRefreshToken({
            self: data.refreshToken.refreshToken,
            expiry: expiryDate,
          });
        },
        onFailure: function (messages: any) {
          console.log("failed in fetching tokens in calling refresh token");
          // Don't immediately clear tokens on initial refresh failure
          // This could be due to timing issues or temporary server problems
          // Only clear if this is not the initial fetch (i.e., it's a scheduled refresh)
          if (!initialFetch) {
            actions.clearAuthToken();
            actions.clearRefreshToken();
          } else {
            console.log(
              "Initial refresh token failure - not clearing tokens, will retry on next interval"
            );
          }
        },
      });
    }

    // Set up the interval and store its ID
    const intervalId = setInterval(() => {
      if (cookie.get(REFRESH_TOKEN)) {
        mutate({
          mutation,
          input: { refreshToken: cookie.get(REFRESH_TOKEN) },
          onSuccess: function (data: any) {
            const exp = data.refreshToken.payload.exp;
            const expiryDate = getExpiryDate(exp);
            actions.updateAuthToken({
              self: data.refreshToken.token,
              expiry: expiryDate,
            });
            actions.updateRefreshToken({
              self: data.refreshToken.refreshToken,
              expiry: expiryDate,
            });
          },
          onFailure: function (messages: any) {
            console.log(
              "failed in fetching tokens in after expiry of authToken",
            );
            // Only clear tokens if this is a scheduled refresh failure
            // This indicates the refresh token is actually invalid/expired
            actions.clearAuthToken();
            actions.clearRefreshToken();
          },
        });
      }
    }, TOKEN_TTL);

    actions.setRefreshIntervalId(intervalId);
  }),
  clearIatas: action((state) => {
    state.iatas = null;
  }),
};

export default AuthStore;
